import { SuitSymbol } from '../common/SuitSymbol';

/**
 * Read-only result panel for the "3rd level bids - Review" page (feature 017). Presents two
 * stacked white boxes (label over a large bold value, matching the Overcalling Data Editing
 * "Jeff's bid"/"Partner" panels): a "Jeff's bid" box always shown (FR-008) and, when Jim answered
 * the hand, a "Jim's answer" box with Correct/Incorrect styling (FR-008a). There is no answer
 * entry and nothing is mutated (FR-003).
 *
 * Presentation-only (constitution III): all data arrives via props.
 */
interface ThirdLevelReviewResultProps {
  /** Jeff's actual decision-turn call — always present on this page. */
  jeffBid: string;
  /** Jim's recorded call for this hand, or null/undefined when Jim has not answered it. */
  jimAnswer?: string | null;
  /** Whether Jim's answer matched Jeff's call (drives the Correct/Incorrect styling). */
  jimAnswerCorrect?: boolean;
}

/** Render a single call with suit symbols: "3S" → 3♠, "3NT" → 3NT, "P" → Pass, "X" → Dbl. */
function ReviewBid({ bid }: { bid: string }) {
  const trimmed = (bid ?? '').trim();
  const upper = trimmed.toUpperCase();

  if (upper === 'PASS' || upper === 'P') return <span className="font-semibold">Pass</span>;
  if (upper === 'DBL' || upper === 'DOUBLE' || upper === 'X') return <span className="font-semibold">Dbl</span>;
  if (upper === 'RDBL' || upper === 'REDOUBLE' || upper === 'XX') return <span className="font-semibold">Rdbl</span>;

  const match = /^([1-7])([CDHS]|NT?)$/.exec(upper);
  if (!match) return <span className="font-semibold">{upper}</span>;

  const level = match[1];
  const suit = match[2];
  if (suit === 'N' || suit === 'NT') {
    return <span className="font-semibold">{level}NT</span>;
  }
  return (
    <span className="font-semibold">
      {level}
      <SuitSymbol suit={suit as 'C' | 'D' | 'H' | 'S'} />
    </span>
  );
}

export function ThirdLevelReviewResult({ jeffBid, jimAnswer, jimAnswerCorrect }: ThirdLevelReviewResultProps) {
  const showJim = jimAnswer != null && jimAnswer !== '';

  return (
    <div className="flex flex-col gap-3 short:gap-2" data-testid="third-level-review-result">
      {/* Jeff's bid — white box with a gray label over a large bold value, matching the
          Overcalling Data Editing "Jeff's bid" panel. Suit symbols keep their red/black colours. */}
      <div className="border border-line bg-card rounded-lg p-3 short:p-2">
        <div className="font-bold text-ink text-base short:text-sm">Jeff&apos;s bid</div>
        <div
          className="mt-2 short:mt-1 text-lg short:text-base font-semibold text-ink"
          data-testid="review-jeff-bid"
        >
          <ReviewBid bid={jeffBid} />
        </div>
      </div>

      {/* Jim's answer — same box style, stacked beneath. The Correct/Incorrect verdict keeps
          its existing green/red colour. */}
      {showJim && (
        <div className="border border-line bg-card rounded-lg p-3 short:p-2" data-testid="review-jim-answer">
          <div className="font-bold text-ink text-base short:text-sm">Jim&apos;s answer</div>
          <div className="mt-2 short:mt-1 text-lg short:text-base font-semibold text-ink">
            <ReviewBid bid={jimAnswer!} />{' '}
            <span className={jimAnswerCorrect ? 'text-green-700' : 'text-red-700'}>
              ({jimAnswerCorrect ? 'Correct' : 'Incorrect'})
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
